import useSWR, { mutate } from 'swr';
import { useCallback, useMemo } from 'react';

import axiosServices, { fetcher } from 'utils/axios';
import useTenantScopedKey from 'hooks/useTenantScopedKey';
import { currentTenantScopedKey } from 'utils/tenantCacheKey';

export interface PeripheralTypeResponse {
  peripheralTypeId: number;
  peripheralTypeName: string;
  defaultConfiguration?: string | null;
  peripheralCategoryId: number;
  isActive: boolean;
}

export interface PeripheralCategoryResponse {
  peripheralCategoryId: number;
  peripheralCategoryName: string;
  dispOrder: number;
  isActive: boolean;
}

export interface PeripheralResponse {
  peripheralId?: string;
  peripheralTypeId: number;
  peripheralName: string;
  configuration?: string | null;
  location?: string | null;
  notes?: string | null;
  linkedId?: number | null;
  siteId?: number;
  isActive: boolean;
}

type UpdatePeripheralTypeRequest = Omit<PeripheralTypeResponse, 'peripheralTypeId'>;
type UpdatePeripheralCategoryRequest = Omit<PeripheralCategoryResponse, 'peripheralCategoryId'>;
type CreatePeripheralTypeRequest = UpdatePeripheralTypeRequest;
type CreatePeripheralCategoryRequest = UpdatePeripheralCategoryRequest;
export type CreatePeripheralRequest = {
  peripheralName: string;
  peripheralTypeId: number;
  location?: string | null;
  configuration?: string | null;
  notes?: string | null;
  linkedId?: number | null;
  siteId: number;
  isActive?: boolean;
};

export type UpdatePeripheralStatusRequest = {
  peripheralId: string;
  isActive: boolean;
};

const endpoints = {
  peripherals: 'api/peripherals',
  categories: 'api/peripheral-categories',
  types: 'api/peripheral-types'
};

function buildEndpoint(base: string, isActive: boolean | null = true): string {
  const separator = base.includes('?') ? '&' : '?';
  if (isActive === null) {
    return `${base}${separator}isActive=`;
  }
  return `${base}${separator}isActive=${isActive}`;
}

function withCacheBuster(url: string, ts: number): string {
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_ts=${ts}`;
}

function normalizeList<T>(payload: unknown): T[] {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (typeof payload !== 'object') {
    return [];
  }

  const record = payload as Record<string, unknown>;
  const envelopeList = (record.items ?? record.data ?? record.results) as unknown;
  if (Array.isArray(envelopeList)) {
    return envelopeList as T[];
  }

  return [];
}

function toCategoryUpdateRequest(category: PeripheralCategoryResponse): UpdatePeripheralCategoryRequest {
  return {
    peripheralCategoryName: category.peripheralCategoryName,
    dispOrder: category.dispOrder,
    isActive: category.isActive
  };
}

function toTypeUpdateRequest(type: PeripheralTypeResponse): UpdatePeripheralTypeRequest {
  return {
    peripheralTypeName: type.peripheralTypeName,
    defaultConfiguration: type.defaultConfiguration ?? null,
    peripheralCategoryId: type.peripheralCategoryId,
    isActive: type.isActive
  };
}

export async function updatePeripheralCategory(
  peripheralCategoryId: number,
  payload: UpdatePeripheralCategoryRequest
): Promise<PeripheralCategoryResponse> {
  const response = await axiosServices.put<PeripheralCategoryResponse>(`${endpoints.categories}/${peripheralCategoryId}`, payload);
  return response.data;
}

export async function updatePeripheralType(
  peripheralTypeId: number,
  payload: UpdatePeripheralTypeRequest
): Promise<PeripheralTypeResponse> {
  const response = await axiosServices.put<PeripheralTypeResponse>(`${endpoints.types}/${peripheralTypeId}`, payload);
  return response.data;
}

export async function createPeripheralCategory(payload: CreatePeripheralCategoryRequest): Promise<PeripheralCategoryResponse> {
  const response = await axiosServices.post<PeripheralCategoryResponse>(endpoints.categories, payload);
  return response.data;
}

export async function createPeripheralType(payload: CreatePeripheralTypeRequest): Promise<PeripheralTypeResponse> {
  const response = await axiosServices.post<PeripheralTypeResponse>(endpoints.types, payload);
  return response.data;
}

export async function createPeripheral(payload: CreatePeripheralRequest): Promise<PeripheralResponse> {
  const response = await axiosServices.post<PeripheralResponse>(endpoints.peripherals, payload);
  return response.data;
}

export async function updatePeripheral(peripheralId: string, payload: CreatePeripheralRequest): Promise<PeripheralResponse> {
  const response = await axiosServices.put<PeripheralResponse>(`${endpoints.peripherals}/${peripheralId}`, payload);
  return response.data;
}

export async function updatePeripheralStatus(peripheralId: string, isActive: boolean): Promise<boolean> {
  const payload = { isActive: isActive };
  const response = await axiosServices.put<boolean>(`${endpoints.peripherals}/status/${peripheralId}`, payload);
  return response.data;
}

export async function savePeripheralCategories(categories: PeripheralCategoryResponse[]): Promise<PeripheralCategoryResponse[]> {
  if (categories.length === 0) {
    return [];
  }

  return Promise.all(
    categories.map((category) => updatePeripheralCategory(category.peripheralCategoryId, toCategoryUpdateRequest(category)))
  );
}

export async function savePeripheralTypes(types: PeripheralTypeResponse[]): Promise<PeripheralTypeResponse[]> {
  if (types.length === 0) {
    return [];
  }

  return Promise.all(types.map((type) => updatePeripheralType(type.peripheralTypeId, toTypeUpdateRequest(type))));
}

export function setPeripheralCategoriesCache(categories: PeripheralCategoryResponse[]) {
  return mutate(currentTenantScopedKey(endpoints.categories), categories, { revalidate: false });
}

export function setPeripheralTypesCache(types: PeripheralTypeResponse[]) {
  return mutate(currentTenantScopedKey(endpoints.types), types, { revalidate: false });
}

export function revalidatePeripherals() {
  const ts = Date.now();

  return Promise.all([
    mutate((key: unknown) => typeof key === 'string' && key.startsWith(endpoints.peripherals)),
    mutate((key: unknown) => typeof key === 'string' && key.startsWith(endpoints.categories)),
    mutate((key: unknown) => typeof key === 'string' && key.startsWith(endpoints.types)),
    mutate(currentTenantScopedKey(buildEndpoint(endpoints.categories)), fetcher(withCacheBuster(buildEndpoint(endpoints.categories), ts)), {
      populateCache: true,
      revalidate: false
    }),
    mutate(
      currentTenantScopedKey(buildEndpoint(endpoints.categories, null)),
      fetcher(withCacheBuster(buildEndpoint(endpoints.categories, null), ts)),
      {
        populateCache: true,
        revalidate: false
      }
    ),
    mutate(currentTenantScopedKey(buildEndpoint(endpoints.types)), fetcher(withCacheBuster(buildEndpoint(endpoints.types), ts)), {
      populateCache: true,
      revalidate: false
    }),
    mutate(
      currentTenantScopedKey(buildEndpoint(endpoints.types, null)),
      fetcher(withCacheBuster(buildEndpoint(endpoints.types, null), ts)),
      {
        populateCache: true,
        revalidate: false
      }
    )
  ]);
}

export function useGetPeripheralsBySite(siteId: number | null, isActive: boolean | null = true) {
  const endpoint = siteId == null ? null : buildEndpoint(`${endpoints.peripherals}/by-site/${siteId}`, isActive);

  const { data, isLoading, error, isValidating } = useSWR<unknown>(useTenantScopedKey(endpoint), () => fetcher(endpoint!), {
    revalidateIfStale: true,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  const revalidatePeripheralsBySite = useCallback(() => {
    if (endpoint == null) return Promise.resolve(undefined);
    const ts = Date.now();
    return mutate(currentTenantScopedKey(endpoint), fetcher(withCacheBuster(endpoint, ts)), { populateCache: true, revalidate: false });
  }, [endpoint]);

  return useMemo(
    () => ({
      peripherals: normalizeList<PeripheralResponse>(data),
      peripheralsLoading: siteId == null ? false : isLoading,
      peripheralsError: error,
      peripheralsValidating: isValidating,
      revalidatePeripheralsBySite
    }),
    [data, error, isLoading, isValidating, siteId, revalidatePeripheralsBySite]
  );
}

export function useGetPeripheralCategories(isActive: boolean | null = true) {
  const endpoint = buildEndpoint(endpoints.categories, isActive);

  const { data, isLoading, error, isValidating } = useSWR<unknown>(useTenantScopedKey(endpoint), () => fetcher(endpoint!), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return useMemo(
    () => ({
      peripheralCategories: normalizeList<PeripheralCategoryResponse>(data).sort((a, b) => a.dispOrder - b.dispOrder),
      peripheralCategoriesLoading: isLoading,
      peripheralCategoriesError: error,
      peripheralCategoriesValidating: isValidating
    }),
    [data, error, isLoading, isValidating]
  );
}

export function useGetPeripheralTypes(isActive: boolean | null = true) {
  const endpoint = buildEndpoint(endpoints.types, isActive);

  const { data, isLoading, error, isValidating } = useSWR<unknown>(useTenantScopedKey(endpoint), () => fetcher(endpoint!), {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });

  return useMemo(
    () => ({
      peripheralTypes: normalizeList<PeripheralTypeResponse>(data).sort((a, b) => a.peripheralTypeName.localeCompare(b.peripheralTypeName)),
      peripheralTypesLoading: isLoading,
      peripheralTypesError: error,
      peripheralTypesValidating: isValidating
    }),
    [data, error, isLoading, isValidating]
  );
}
